/**
 * /apps/:appId/setup/roles/:roleId
 *
 * Single-save role definition page.
 *
 * Layout philosophy: the editing surface itself is rendered as an A4-style
 * sheet of paper. The save bar is sticky above the sheet; the sheet
 * contains all content in a clean document order so it prints (Cmd-P)
 * directly without needing a separate route. A dedicated print view at
 * .../print is also available for a fully chrome-free document.
 *
 * Sections (top of sheet → bottom):
 *   1. Header block       — title + code + identity meta (no "Identity" label)
 *   2. Purpose of this role  (single textarea, was the description field)
 *   3. Responsibility Areas  — TABLE: # | Area | Description | Performance Parameters (chips)
 *   4. Major Routine Activities  — flat numbered list with periodicity per row
 *   5. Skills required    — numbered list with autocomplete + inline-create
 *   6. Education          — numbered list
 *   7. Experience         — numbered list
 *   8. Best Performance Definition  — single textarea
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ConfirmDeleteModal from '../../components/workspace/ConfirmDeleteModal';
import { SearchableSelect } from '../../components/workspace/filters/FilterDropdowns';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';

const EMPLOYMENT_OPTIONS = [
  { value: 'full_time',  label: 'Full-time' },
  { value: 'part_time',  label: 'Part-time' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'intern',     label: 'Intern' },
  { value: 'consultant', label: 'Consultant' },
];
const PERIODICITIES = [
  { value: 'daily',     label: 'Daily' },
  { value: 'weekly',    label: 'Weekly' },
  { value: 'monthly',   label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly',    label: 'Yearly' },
];
const SKILL_KINDS = [
  { value: 'hard',     label: 'Hard' },
  { value: 'soft',     label: 'Soft' },
  { value: 'language', label: 'Language' },
  { value: 'tool',     label: 'Tool' },
  { value: 'other',    label: 'Other' },
];
const PROFICIENCY_OPTIONS = [1, 2, 3, 4, 5].map((p) => ({ value: p, label: `${p}/5` }));

let _tempCounter = -1;
const nextTempId = () => _tempCounter--;

const emptyArea    = () => ({ _key: nextTempId(), id: null, name: '', description: '', performance_parameters: [] });
const emptyParam   = () => ({ _key: nextTempId(), id: null, parameter: '' });
const emptyRoutine = () => ({ _key: nextTempId(), id: null, description: '', periodicity: 'weekly' });
const emptySkill   = () => ({ _key: nextTempId(), id: null, skill_id: null, skill_name: '', skill_kind: 'hard', required_proficiency: 3, is_mandatory: true });
const emptyEdu     = () => ({ _key: nextTempId(), id: null, level: '', field: '', is_required: true });
const emptyExp     = () => ({ _key: nextTempId(), id: null, years_required: '', type: '', description: '' });

function withKeys(tree) {
  if (!tree) return tree;
  const stamp = (row) => ({ ...row, _key: row.id ?? nextTempId() });
  return {
    ...tree,
    areas: (tree.areas || []).map((a) => ({
      ...stamp(a),
      performance_parameters: (a.performance_parameters || []).map(stamp),
    })),
    routines:   (tree.routines   || []).map(stamp),
    skills:     (tree.skills     || []).map(stamp),
    education:  (tree.education  || []).map(stamp),
    experience: (tree.experience || []).map(stamp),
  };
}

function stripKeys(tree) {
  const cleanRow = ({ _key, ...rest }) => rest; // eslint-disable-line no-unused-vars
  return {
    ...tree,
    areas: tree.areas.map((a) => ({
      ...cleanRow(a),
      performance_parameters: a.performance_parameters.filter(p => p.parameter?.trim()).map(cleanRow),
    })),
    routines:   tree.routines.map(cleanRow),
    skills:     tree.skills.map(cleanRow),
    education:  tree.education.map(cleanRow),
    experience: tree.experience.map(cleanRow),
  };
}

// =========================================================================

export default function RoleDetail({ roleId: propRoleId, printPath, onPrint }) {
  const { appId, roleId: paramRoleId } = useParams();
  const roleId = propRoleId || paramRoleId;
  const navigate = useNavigate();
  const api = useApi();
  const { canWrite, organisation } = useWorkspace();

  const [tree, setTree]             = useState(null);
  const [serverTree, setServerTree] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState(null);
  const [savedAt, setSavedAt]       = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [showView,   setShowView]   = useState(false);

  const [departments, setDepartments] = useState([]);
  const [allRoles, setAllRoles]       = useState([]);
  const [skillCatalogue, setSkillCatalogue] = useState([]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [t, ds, rs, sk] = await Promise.all([
        api.getRoleFull(appId, roleId),
        api.departments.list(appId).catch(() => ({ items: [] })),
        api.roles.list(appId).catch(() => ({ items: [] })),
        api.skills.list(appId).catch(() => ({ items: [] })),
      ]);
      const stamped = withKeys(t);
      setTree(stamped);
      setServerTree(stamped);
      setDepartments(ds.items || []);
      setAllRoles(rs.items || []);
      setSkillCatalogue(sk.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load role');
    } finally {
      setLoading(false);
    }
  }, [api, appId, roleId]);

  useEffect(() => { load(); }, [load]);

  const isDirty = useMemo(() => {
    if (!tree || !serverTree) return false;
    return JSON.stringify(stripKeys(tree)) !== JSON.stringify(stripKeys(serverTree));
  }, [tree, serverTree]);

  const otherRoles = allRoles.filter((r) => r.id !== tree.id);

  const validateTree = (t) => {
    for (let i = 0; i < t.skills.length; i++) {
      const s = t.skills[i];
      if (s.is_mandatory && !s.skill_name?.trim())
        return `Skill #${i + 1} is marked as Mandatory — skill name is required`;
    }
    for (let i = 0; i < t.education.length; i++) {
      const e = t.education[i];
      if (e.is_required && !e.level?.trim())
        return `Education #${i + 1} is marked as Required — Level is required`;
      if (e.is_required && !e.field?.trim())
        return `Education #${i + 1} is marked as Required — Field of study is required`;
    }
    for (let i = 0; i < t.experience.length; i++) {
      const e = t.experience[i];
      const hasYears = e.years_required !== '' && e.years_required != null;
      if (!e.type?.trim() && !e.description?.trim() && !hasYears)
        return `Experience entry #${i + 1} requires at least one of: type, description, or years required`;
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateTree(tree);
    if (validationError) { setError(validationError); return; }
    setError(null);
    setSaving(true);
    try {
      const updated = await api.saveRoleFull(appId, roleId, stripKeys(tree));
      const stamped = withKeys(updated);
      setTree(stamped);
      setServerTree(stamped);
      setSavedAt(new Date());
      api.skills.list(appId).then((sk) => setSkillCatalogue(sk.items || [])).catch(() => {});
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!serverTree) return;
    setTree(serverTree);
    setError(null);
  };

  if (loading) return <p className="muted" style={{ padding: 24 }}>Loading role…</p>;
  if (!tree)   return <div className="banner banner-error" style={{ margin: 24 }}>{error || 'Role not found'}</div>;

  // ---- top-level setters ----
  const setField = (k, v) => setTree((t) => ({ ...t, [k]: v }));

  // ---- areas ----
  const updateArea = (i, patch) =>
    setTree((t) => {
      const areas = t.areas.slice();
      areas[i] = { ...areas[i], ...patch };
      return { ...t, areas };
    });
  const addArea    = () => setTree((t) => ({ ...t, areas: [...t.areas, emptyArea()] }));
  const removeArea = (i) => setTree((t) => ({ ...t, areas: t.areas.filter((_, idx) => idx !== i) }));

  // ---- area params ----
  const addParam = (areaIdx, parameter) => {
    const text = (parameter || '').trim();
    if (!text) return;
    setTree((t) => {
      const areas = t.areas.slice();
      const area = areas[areaIdx];
      areas[areaIdx] = { ...area, performance_parameters: [...area.performance_parameters, { ...emptyParam(), parameter: text }] };
      return { ...t, areas };
    });
  };
  const updateParam = (areaIdx, paramIdx, patch) =>
    setTree((t) => {
      const areas = t.areas.slice();
      const area = areas[areaIdx];
      const params = area.performance_parameters.slice();
      params[paramIdx] = { ...params[paramIdx], ...patch };
      areas[areaIdx] = { ...area, performance_parameters: params };
      return { ...t, areas };
    });
  const removeParam = (areaIdx, paramIdx) =>
    setTree((t) => {
      const areas = t.areas.slice();
      const area = areas[areaIdx];
      areas[areaIdx] = { ...area, performance_parameters: area.performance_parameters.filter((_, idx) => idx !== paramIdx) };
      return { ...t, areas };
    });

  // ---- routines (flat) ----
  const updateRoutine = (i, patch) =>
    setTree((t) => {
      const routines = t.routines.slice();
      routines[i] = { ...routines[i], ...patch };
      return { ...t, routines };
    });
  const addRoutine    = () => setTree((t) => ({ ...t, routines: [...t.routines, emptyRoutine()] }));
  const removeRoutine = (i) => setTree((t) => ({ ...t, routines: t.routines.filter((_, idx) => idx !== i) }));

  // ---- skills ----
  const updateSkill = (i, patch) =>
    setTree((t) => {
      const skills = t.skills.slice();
      skills[i] = { ...skills[i], ...patch };
      return { ...t, skills };
    });
  const addSkill    = () => setTree((t) => ({ ...t, skills: [...t.skills, emptySkill()] }));
  const removeSkill = (i) => setTree((t) => ({ ...t, skills: t.skills.filter((_, idx) => idx !== i) }));

  // ---- education / experience ----
  const updateEdu = (i, patch) => setTree((t) => { const a = t.education.slice(); a[i] = { ...a[i], ...patch }; return { ...t, education: a }; });
  const addEdu    = () => setTree((t) => ({ ...t, education: [...t.education, emptyEdu()] }));
  const removeEdu = (i) => setTree((t) => ({ ...t, education: t.education.filter((_, idx) => idx !== i) }));

  const updateExp = (i, patch) => setTree((t) => { const a = t.experience.slice(); a[i] = { ...a[i], ...patch }; return { ...t, experience: a }; });
  const addExp    = () => setTree((t) => ({ ...t, experience: [...t.experience, emptyExp()] }));
  const removeExp = (i) => setTree((t) => ({ ...t, experience: t.experience.filter((_, idx) => idx !== i) }));

  return (
    <div className="role-page">
      {error && <div className="banner banner-error no-print">{error}</div>}

      {/* Page-level action row above the sheet */}
      <div className="role-page-actions no-print">
        {!propRoleId && (
          <Link className="btn-back" to={`/apps/${appId}/setup/roles`}>←</Link>
        )}
        {isDirty && <span className="dirty-pill">Unsaved</span>}
        {!isDirty && savedAt && <span className="muted small">Saved at {savedAt.toLocaleTimeString()}</span>}
        <div style={{ flex: 1 }} />
        <button className="btn-secondary" onClick={() => setShowView(true)}>View</button>
        {canWrite && (
          <>
            <button className="btn-secondary" onClick={handleDiscard} disabled={saving || !isDirty}>Cancel</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving || !isDirty}>
              {saving ? 'Saving…' : isDirty ? 'Save Changes' : 'Saved'}
            </button>
            {!propRoleId && (
              <button className="btn-link-sm danger" onClick={() => setDeleteOpen(true)}>Deactivate</button>
            )}
          </>
        )}
      </div>

      {/* The A4 sheet itself */}
      <article className="role-sheet">
        {/* Sheet header — org name right, doc type left */}
        <header className="role-sheet-header">
          <span className="role-sheet-doc-type">Role Definition</span>
          {organisation?.name && <span className="role-sheet-org-name">{organisation.name}</span>}
        </header>

        {/* Title block */}
        <section className="role-title-block">
          <div className="role-title-hero">
            <div className="role-avatar-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8"/>
                <path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>
            <input
              className="role-title-input"
              value={tree.title || ''}
              onChange={(e) => setField('title', e.target.value)}
              disabled={!canWrite}
              maxLength={200}
              placeholder="Role title"
            />
          </div>
          <div className="role-title-meta-grid">
            <MetaInput
              label="Code"
              value={tree.code || ''}
              onChange={(v) => setField('code', v)}
              disabled={!canWrite}
              maxLength={50}
              placeholder="e.g. CTO"
            />
            <MetaSelect
              label="Department"
              value={tree.department_id ?? ''}
              onChange={(v) => setField('department_id', v === '' ? null : Number(v))}
              disabled={!canWrite}
              options={[
                { value: '', label: '— none —' },
                ...departments.map((d) => ({ value: d.id, label: d.name })),
              ]}
            />
            <MetaInput
              label="Level"
              type="number"
              min={0}
              max={20}
              value={tree.level ?? 0}
              onChange={(v) => setField('level', Number(v))}
              disabled={!canWrite}
            />
            <MetaSelect
              label="Employment"
              value={tree.employment_type || 'full_time'}
              onChange={(v) => setField('employment_type', v)}
              disabled={!canWrite}
              options={EMPLOYMENT_OPTIONS}
            />
            {!propRoleId && (
              <MetaInput
                label="Max Yearly CTC"
                type="number"
                min={0}
                max={100000000}
                step="0.01"
                value={tree.max_yearly_ctc ?? ''}
                onChange={(v) => setField('max_yearly_ctc', v === '' ? null : Number(v))}
                disabled={!canWrite}
                placeholder="Annual cost-to-company"
              />
            )}
            <MetaSelect
              label="Reports To"
              value={tree.reports_to_role_id ?? ''}
              onChange={(v) => setField('reports_to_role_id', v === '' ? null : Number(v))}
              disabled={!canWrite}
              options={[
                { value: '', label: '— none —' },
                ...otherRoles.map((r) => ({ value: r.id, label: r.title })),
              ]}
            />
          </div>
        </section>

        {/* Purpose of this role */}
        <Section title="Purpose of this role">
          <textarea
            className="form-input role-purpose"
            rows={3}
            maxLength={5000}
            value={tree.description || ''}
            onChange={(e) => setField('description', e.target.value)}
            disabled={!canWrite}
            placeholder="Why does this role exist? One or two sentences."
          />
        </Section>

        {/* Responsibility Areas — TABLE format */}
        <Section
          title="Responsibility Areas"
          action={canWrite && <button className="btn-link-sm" onClick={addArea}>+ Add area</button>}
        >
          {tree.areas.length === 0 ? (
            <p className="muted small">No areas yet.</p>
          ) : (
            <table className="responsibilities-table">
              <thead>
                <tr>
                  <th style={{ whiteSpace: 'nowrap' }}>Sl No</th>
                  <th style={{ width: '20%' }}>Responsibility Area</th>
                  <th style={{ width: '30%' }}>Description</th>
                  <th>Performance Parameters</th>
                  {canWrite && <th style={{ width: 28 }} className="no-print"></th>}
                </tr>
              </thead>
              <tbody>
                {tree.areas.map((area, idx) => (
                  <tr key={area._key}>
                    <td className="serial-cell">{pad2(idx)}</td>
                    <td>
                      <textarea
                        className="flush-textarea"
                        value={area.name || ''}
                        onChange={(e) => updateArea(idx, { name: e.target.value })}
                        placeholder="Area name"
                        disabled={!canWrite}
                        maxLength={200}
                      />
                    </td>
                    <td>
                      <textarea
                        className="flush-textarea"
                        value={area.description || ''}
                        onChange={(e) => updateArea(idx, { description: e.target.value })}
                        placeholder="Description"
                        disabled={!canWrite}
                      />
                    </td>
                    <td>
                      <ParamsCell
                        params={area.performance_parameters}
                        canEdit={canWrite}
                        onAdd={(text) => addParam(idx, text)}
                        onUpdate={(pIdx, patch) => updateParam(idx, pIdx, patch)}
                        onRemove={(pIdx) => removeParam(idx, pIdx)}
                      />
                    </td>
                    {canWrite && (
                      <td className="no-print" style={{ textAlign: 'center' }}>
                        <button
                          className="btn-link-sm danger small"
                          onClick={() => removeArea(idx)}
                          title="Remove area"
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        {/* Major Routine Activities — flat */}
        <Section
          title="Major Routine Activities"
          action={canWrite && <button className="btn-link-sm" onClick={addRoutine}>+ Add routine</button>}
        >
          {tree.routines.length === 0 ? (
            <p className="muted small">None yet.</p>
          ) : (
            <ul className="numbered-list">
              {tree.routines.map((r, idx) => (
                <li key={r._key} className="numbered-row">
                  <span className="serial">{String(idx + 1).padStart(2, '0')} </span>
                  <input
                    className="form-input"
                    placeholder="e.g. Tech all-hands"
                    value={r.description || ''}
                    onChange={(e) => updateRoutine(idx, { description: e.target.value })}
                    disabled={!canWrite}
                    maxLength={500}
                  />
                  <SearchableSelect
                    value={r.periodicity}
                    onChange={(v) => updateRoutine(idx, { periodicity: v })}
                    options={PERIODICITIES}
                    showAll={false}
                    disabled={!canWrite}
                    btnClass="form-input small rd-select-btn"
                    matchTriggerWidth
                    wrapStyle={{ width: 'auto', flexShrink: 0, minWidth: 110 }}
                  />
                  {canWrite && (
                    <button className="btn-link-sm danger small no-print" onClick={() => removeRoutine(idx)}>×</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Skills required */}
        <Section
          title="Skills required"
          action={canWrite && <button className="btn-link-sm" onClick={addSkill}>+ Add skill</button>}
        >
          {tree.skills.length === 0 ? (
            <p className="muted small">No skills declared.</p>
          ) : (
            <ul className="numbered-list">
              {tree.skills.map((s, idx) => (
                <SkillRow
                  key={s._key}
                  serial={idx + 1}
                  skill={s}
                  catalogue={skillCatalogue}
                  canEdit={canWrite}
                  onChange={(patch) => updateSkill(idx, patch)}
                  onRemove={() => removeSkill(idx)}
                />
              ))}
            </ul>
          )}
        </Section>

        {/* Education */}
        <Section
          title="Education"
          action={canWrite && <button className="btn-link-sm" onClick={addEdu}>+ Add</button>}
        >
          {tree.education.length === 0 ? (
            <p className="role-empty-state">No education requirements declared.</p>
          ) : (
            <ul className="numbered-list">
              {tree.education.map((e, idx) => (
                <li key={e._key} className="numbered-row">
                  <span className="serial">{pad2(idx)}</span>
                  <input
                    className="form-input"
                    placeholder="Level (e.g. Bachelors)"
                    list="edu-levels"
                    value={e.level || ''}
                    onChange={(ev) => updateEdu(idx, { level: ev.target.value })}
                    disabled={!canWrite}
                  />
                  <input
                    className="form-input"
                    placeholder="Field"
                    value={e.field || ''}
                    onChange={(ev) => updateEdu(idx, { field: ev.target.value })}
                    disabled={!canWrite}
                  />
                  <label className="checkbox-row small">
                    <input
                      type="checkbox"
                      checked={!!e.is_required}
                      disabled={!canWrite}
                      onChange={(ev) => updateEdu(idx, { is_required: ev.target.checked })}
                    />
                    Required
                  </label>
                  {canWrite && (
                    <button className="btn-link-sm danger small no-print" onClick={() => removeEdu(idx)}>×</button>
                  )}
                </li>
              ))}
              <datalist id="edu-levels">
                {['High school', 'Diploma', 'Bachelors', 'Masters', 'PhD', 'Certification'].map((l) =>
                  <option key={l} value={l} />
                )}
              </datalist>
            </ul>
          )}
        </Section>

        {/* Experience */}
        <Section
          title="Experience"
          action={canWrite && <button className="btn-link-sm" onClick={addExp}>+ Add</button>}
        >
          {tree.experience.length === 0 ? (
            <p className="role-empty-state">No experience requirements declared.</p>
          ) : (
            <ul className="numbered-list">
              {tree.experience.map((e, idx) => (
                <li key={e._key} className="numbered-row">
                  <span className="serial">{pad2(idx)}</span>
                  <input
                    type="number"
                    className="form-input small"
                    min={0}
                    max={80}
                    step="0.1"
                    placeholder="Years"
                    value={e.years_required ?? ''}
                    onChange={(ev) => updateExp(idx, { years_required: ev.target.value })}
                    disabled={!canWrite}
                  />
                  <input
                    className="form-input"
                    placeholder="Type (e.g. people management)"
                    value={e.type || ''}
                    onChange={(ev) => updateExp(idx, { type: ev.target.value })}
                    disabled={!canWrite}
                  />
                  <input
                    className="form-input"
                    placeholder="More detail (optional)"
                    value={e.description || ''}
                    onChange={(ev) => updateExp(idx, { description: ev.target.value })}
                    disabled={!canWrite}
                  />
                  {canWrite && (
                    <button className="btn-link-sm danger small no-print" onClick={() => removeExp(idx)}>×</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>

        {/* Best Performance */}
        <Section title="Best Performance Definition">
          <textarea
            className="form-input role-best-perf"
            rows={4}
            maxLength={20000}
            value={tree.best_performance_md || ''}
            onChange={(e) => setField('best_performance_md', e.target.value)}
            disabled={!canWrite}
            placeholder="What does excellent performance look like in this role?"
          />
        </Section>

      </article>

      <ConfirmDeleteModal
        open={deleteOpen}
        title={`Deactivate role "${tree.title}"?`}
        message="It can be reactivated later from the Roles list. Blocked while anyone still holds this role — unassign it from them first."
        confirmLabel="Deactivate"
        onConfirm={async () => {
          try {
            await api.roles.remove(appId, roleId);
            navigate(`/apps/${appId}/setup/roles`);
          } catch (err) {
            const data = err.response?.data;
            if (data?.code === 'role_has_holders') {
              const names = (data.holders || []).map((h) => h.name).filter(Boolean);
              throw new Error(names.length ? `${data.error} Currently held by: ${names.join(', ')}.` : data.error);
            }
            throw err;
          }
        }}
        onClose={() => setDeleteOpen(false)}
      />

      {showView && (
        <RoleViewModal
          tree={tree}
          departments={departments}
          allRoles={allRoles}
          organisation={organisation}
          onClose={() => setShowView(false)}
        />
      )}
    </div>
  );
}

// ============================================================ sub-components

const SECTION_ICONS = {
  'Purpose of this role': <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/><path d="M12 8v4l2.5 2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>,
  'Responsibility Areas': <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M3 9h18M9 9v12" stroke="currentColor" strokeWidth="1.8"/></svg>,
  'Major Routine Activities': <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/><path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>,
  'Skills required': <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>,
  'Education': <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M22 10v6M2 10l10-5 10 5-10 5-10-5z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 12v5c0 1.66 2.686 3 6 3s6-1.34 6-3v-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>,
  'Experience': <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.8"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" stroke="currentColor" strokeWidth="1.8"/></svg>,
  'Best Performance Definition': <svg viewBox="0 0 24 24" fill="none" width="14" height="14"><path d="M9 11l3 3L22 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>,
};

const META_ICONS = {
  'Code':          <svg viewBox="0 0 24 24" fill="none" width="16" height="16"><rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.6"/><path d="M8 9l-3 3 3 3M16 9l3 3-3 3M13 7l-2 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  'Department':    <svg viewBox="0 0 24 24" fill="none" width="16" height="16"><path d="M3 21h18M9 21V7l6-4v18M9 11h6M9 16h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  'Level':         <svg viewBox="0 0 24 24" fill="none" width="16" height="16"><path d="M3 18h4v-6H3v6zM10 18h4V8h-4v10zM17 18h4V4h-4v14z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>,
  'Employment':    <svg viewBox="0 0 24 24" fill="none" width="16" height="16"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.6"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  'Max Yearly CTC':<svg viewBox="0 0 24 24" fill="none" width="16" height="16"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6"/><path d="M9 14.5c0 1.1.9 2 2 2h2a2 2 0 000-4h-2a2 2 0 010-4h2a2 2 0 012 2M12 7v1m0 8v1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>,
  'Reports To':    <svg viewBox="0 0 24 24" fill="none" width="16" height="16"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.6"/><path d="M21 15l-3-3-3 3M18 12v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
};

function Section({ title, action, children }) {
  return (
    <section className="role-doc-section">
      <div className="role-doc-section-head">
        <h2>
          {SECTION_ICONS[title] && (
            <span className="role-section-icon">{SECTION_ICONS[title]}</span>
          )}
          {title}
        </h2>
        {action && <span className="no-print">{action}</span>}
      </div>
      {children}
    </section>
  );
}

function MetaInput({ label, value, onChange, disabled, ...rest }) {
  return (
    <label className="meta-field">
      <span className="meta-label">
        {META_ICONS[label] && <span className="meta-icon">{META_ICONS[label]}</span>}
        {label}
      </span>
      <input
        className="form-input flush"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        {...rest}
      />
    </label>
  );
}

function MetaSelect({ label, value, onChange, disabled, options }) {
  return (
    <label className="meta-field">
      <span className="meta-label">
        {META_ICONS[label] && <span className="meta-icon">{META_ICONS[label]}</span>}
        {label}
      </span>
      <SearchableSelect
        value={value ?? ''}
        onChange={onChange}
        options={options}
        showAll={false}
        disabled={disabled}
        btnClass="form-input flush rd-select-btn"
        matchTriggerWidth
      />
    </label>
  );
}

function ParamsCell({ params, canEdit, onAdd, onUpdate, onRemove }) {
  const [draft, setDraft] = useState('');
  const commit = () => {
    if (draft.trim()) {
      onAdd(draft.trim());
      setDraft('');
    }
  };
  return (
    <div className="params-cell">
      {params.map((p, idx) => (
        <div key={p._key} className="param-bullet-row">
          <span className="param-bullet">•</span>
          {canEdit ? (
            <input
              className="param-bullet-input"
              value={p.parameter || ''}
              onChange={(e) => onUpdate(idx, { parameter: e.target.value })}
              placeholder="parameter"
            />
          ) : (
            <span className="param-bullet-text">{p.parameter}</span>
          )}
          {canEdit && (
            <button
              type="button"
              className="param-bullet-remove no-print"
              onClick={() => onRemove(idx)}
              title="Remove"
            >×</button>
          )}
        </div>
      ))}
      {canEdit && (params.length === 0 || params[params.length - 1]?.parameter?.trim()) && (
        <div className="param-add-row no-print">
          <input
            className="param-add-input"
            placeholder="+ add parameter"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              if (e.key === 'Escape') setDraft('');
            }}
            onBlur={() => { if (draft.trim()) commit(); }}
          />
        </div>
      )}
    </div>
  );
}

function SkillRow({ serial, skill, catalogue, canEdit, onChange, onRemove }) {
  const handleNameChange = (newName) => {
    const trimmed = newName.trim();
    const match = catalogue.find((c) => c.name.toLowerCase() === trimmed.toLowerCase());
    onChange({
      skill_name: newName,
      skill_id: match ? match.id : null,
      ...(match && match.kind ? { skill_kind: match.kind } : {}),
    });
  };

  return (
    <li className="skill-row-edit">
      <span className="serial skill-serial">{String(serial).padStart(2, '0')}</span>
      <div className="skill-name-wrap">
        <input
          className="form-input"
          placeholder="Skill name"
          value={skill.skill_name || ''}
          onChange={(e) => handleNameChange(e.target.value)}
          disabled={!canEdit}
          list="skill-catalogue-edit"
        />
        <datalist id="skill-catalogue-edit">
          {catalogue.map((c) => <option key={c.id} value={c.name} />)}
        </datalist>
      </div>
      <SearchableSelect
        value={skill.skill_kind || 'hard'}
        onChange={(v) => onChange({ skill_kind: v })}
        options={SKILL_KINDS}
        showAll={false}
        disabled={!canEdit}
        title="Type"
        btnClass="form-input flush skill-select rd-select-btn"
        matchTriggerWidth
        wrapStyle={{ width: 'auto', flexShrink: 0, minWidth: 90 }}
      />
      <SearchableSelect
        value={skill.required_proficiency || 3}
        onChange={(v) => onChange({ required_proficiency: Number(v) })}
        options={PROFICIENCY_OPTIONS}
        showAll={false}
        disabled={!canEdit}
        title="Proficiency"
        btnClass="form-input flush skill-select rd-select-btn"
        matchTriggerWidth
        wrapStyle={{ width: 'auto', flexShrink: 0, minWidth: 90 }}
      />
      <label className="checkbox-row small skill-mandatory">
        <input
          type="checkbox"
          checked={!!skill.is_mandatory}
          disabled={!canEdit}
          onChange={(e) => onChange({ is_mandatory: e.target.checked })}
        />
        Mandatory
      </label>
      {canEdit && (
        <button className="btn-link-sm danger small no-print skill-delete" onClick={onRemove} title="Remove skill">
          <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"/></svg>
        </button>
      )}
    </li>
  );
}

// ============================================================ RoleViewModal

const EMPLOYMENT_LABEL = {
  full_time: 'Full-time', part_time: 'Part-time', contractor: 'Contractor',
  intern: 'Intern', consultant: 'Consultant',
};
const PERIODICITY_LABEL = {
  daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly',
  quarterly: 'Quarterly', yearly: 'Yearly',
};
const KIND_LABEL = {
  hard: 'Hard skill', soft: 'Soft skill', language: 'Language',
  tool: 'Tool', other: 'Other',
};

function pad2(n) { return String(n + 1).padStart(2, '0'); }

function RoleViewModal({ tree, departments, allRoles, organisation, onClose }) {
  const contentRef = useRef(null);

  const dept      = departments.find((d) => d.id === tree.department_id);
  const reportsTo = allRoles.find((r) => r.id === tree.reports_to_role_id);

  const fields = [
    tree.code              && { label: 'Code',           value: tree.code },
    dept                   && { label: 'Department',      value: dept.name },
    tree.level != null     && { label: 'Level',           value: tree.level },
    tree.employment_type   && { label: 'Employment',      value: EMPLOYMENT_LABEL[tree.employment_type] || tree.employment_type },
    tree.max_yearly_ctc != null && { label: 'Max Yearly CTC', value: new Intl.NumberFormat().format(tree.max_yearly_ctc) },
    reportsTo              && { label: 'Reports To',      value: reportsTo.title },
  ].filter(Boolean);

  const handlePrint = () => {
    const el = contentRef.current;
    if (!el) return;
    const html = el.innerHTML;
    const win = window.open('', '_blank', 'width=900,height=700');
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>Role Definition</title>
      <style>
        :root {
          --bg-page: #f4f5f7;
          --bg-card: #ffffff;
          --bg-muted: #f1f3f5;
          --border: #e2e6ea;
          --text-primary: #1f2933;
          --text-secondary: #3e4c59;
          --text-muted: #7b8794;
          --brand: #06b6d4;
          --accent-soft: rgba(6,182,212,0.10);
          --accent-line: rgba(6,182,212,0.25);
        }
        * { box-sizing: border-box; max-width: 100%; }
        body {
          margin: 0;
          padding: 14mm 16mm;
          background: #ffffff;
          font-family: system-ui, sans-serif;
          color: #1f2933;
          font-size: 13px;
          line-height: 1.5;
          word-break: break-word;
          overflow-wrap: anywhere;
          overflow-x: hidden;
          width: 100%;
        }
        img, svg { max-width: 100%; height: auto; }
        p, span, div { word-break: break-word; overflow-wrap: anywhere; }
        p { margin: 0 0 8px; }
        strong { font-weight: 700; }
        ul { margin: 4px 0 0; padding-left: 18px; }
        @page { margin: 14mm 16mm; size: A4 portrait; }
        @media print {
          body { padding: 14mm 16mm; }
        }
      </style>
    </head><body>${html}</body></html>`);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  // Close on backdrop click
  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
      onClick={handleBackdrop}
    >
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        width: '100%',
        maxWidth: 780,
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'var(--shadow-card)',
        overflow: 'hidden',
      }}>
        {/* Modal header bar */}
        <div className="rv-modal-header-bar" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-muted)',
          flexShrink: 0,
        }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>Role Overview</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-primary" style={{ fontSize: 12, padding: '5px 14px' }} onClick={handlePrint}>
              🖨 Print / Save as PDF
            </button>
            <button onClick={onClose} style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: '2px 6px',
            }}>×</button>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="rv-modal-body" ref={contentRef} style={{
          overflowY: 'auto',
          padding: '28px 32px',
          flex: 1,
        }}>
          {/* Org header */}
          <div style={{
            display: 'flex', alignItems: 'center',
            borderBottom: '1px solid var(--border)',
            paddingBottom: 14, marginBottom: 24,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1.5 }}>
                Role Overview
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand)', textTransform: 'uppercase', letterSpacing: 1.2 }}>
                <span data-print-org="">{organisation?.name || ''}</span>
              </div>
            </div>
          </div>

          {/* Role title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
            <div style={{
              width: 52, height: 52, borderRadius: '50%',
              background: 'var(--accent-soft)', border: '1px solid var(--accent-line)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="8" r="4" stroke="var(--brand)" strokeWidth="1.8"/>
                <path d="M4 20c0-4 3.582-7 8-7s8 3 8 7" stroke="var(--brand)" strokeWidth="1.8" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: 0.3 }}>
                {tree.title || 'Untitled Role'}
              </div>
              {tree.code && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginTop: 2 }}>
                  {tree.code}
                </div>
              )}
            </div>
          </div>

          {/* Identity fields */}
          {fields.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 10, marginBottom: 28,
            }}>
              {fields.map((f) => (
                <div key={f.label} style={{
                  background: 'var(--bg-muted)',
                  border: '1px solid var(--border)',
                  borderRadius: 8, padding: '10px 14px',
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.2, color: 'var(--text-muted)', marginBottom: 4 }}>
                    {f.label}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                    {f.value}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Purpose */}
          <ViewSection title="Purpose of This Role">
            {tree.description
              ? <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{tree.description}</p>
              : <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>Why does this role exist? One or two sentences.</p>
            }
          </ViewSection>

          {/* Responsibility Areas */}
          {tree.areas?.length > 0 && (
            <ViewSection title="Responsibility Areas">
              <ViewList items={tree.areas.map((a, i) => (
                <span>
                  <strong>{a.name}</strong>
                  {a.description && <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>— {a.description}</span>}
                  {a.performance_parameters?.length > 0 && (
                    <ul style={{ margin: '4px 0 0', paddingLeft: 16, color: 'var(--text-muted)', fontSize: 12 }}>
                      {a.performance_parameters.map((p) => <li key={p.id || p._key}>{p.parameter}</li>)}
                    </ul>
                  )}
                </span>
              ))} />
            </ViewSection>
          )}

          {/* Routines */}
          {tree.routines?.length > 0 && (
            <ViewSection title="Major Routine Activities">
              <ViewList items={tree.routines.map((r) => (
                <span>
                  <strong>{r.description}</strong>
                  {r.periodicity && <span style={{ color: 'var(--text-muted)', marginLeft: 6, fontSize: 12 }}>– {PERIODICITY_LABEL[r.periodicity] || r.periodicity}</span>}
                </span>
              ))} />
            </ViewSection>
          )}

          {/* Skills */}
          {tree.skills?.length > 0 && (
            <ViewSection title="Skills Required">
              <ViewList items={tree.skills.map((s) => (
                <span>
                  <strong>{s.skill_name}</strong>
                  {s.skill_kind && <ViewTag>{KIND_LABEL[s.skill_kind] || s.skill_kind}</ViewTag>}
                  {s.required_proficiency != null && (
                    <span style={{ color: 'var(--text-secondary)', marginLeft: 8, fontSize: 12 }}>
                      Proficiency: <strong style={{ color: 'var(--text-primary)' }}>{s.required_proficiency}/5</strong>
                    </span>
                  )}
                </span>
              ))} />
            </ViewSection>
          )}

          {/* Education */}
          {tree.education?.length > 0 && (
            <ViewSection title="Education">
              <ViewList items={tree.education.map((e) => (
                <span>
                  {e.level && <strong>{e.level}</strong>}
                  {e.level && e.field && ' in '}
                  {e.field}
                  {e.min_gpa && <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}><strong style={{ color: 'var(--text-primary)' }}>{e.min_gpa}</strong> GPA</span>}
                </span>
              ))} />
            </ViewSection>
          )}

          {/* Experience */}
          <ViewSection title="Experience">
            {tree.experience?.length > 0 ? (
              <ViewList items={tree.experience.map((e) => (
                <span>
                  {e.years_required != null && e.years_required !== '' && <strong>{Number(e.years_required)}+ years</strong>}
                  {e.type && <span style={{ color: 'var(--text-secondary)', marginLeft: 6 }}>{e.type}</span>}
                  {e.description && <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>— {e.description}</span>}
                </span>
              ))} />
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No experience requirements declared.</p>
            )}
          </ViewSection>

          {/* Best Performance */}
          {tree.best_performance_md && (
            <ViewSection title="Best Performance Definition">
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{tree.best_performance_md}</p>
            </ViewSection>
          )}
        </div>
      </div>
    </div>
  );
}

function ViewSection({ title, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 1.5, color: 'var(--brand)',
        borderBottom: '1px solid var(--border)',
        paddingBottom: 6, marginBottom: 10,
      }}>{title}</div>
      {children}
    </div>
  );
}

function ViewList({ items }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((item, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          background: 'var(--bg-muted)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)',
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', minWidth: 24, paddingTop: 1 }}>
            {pad2(i)}.
          </span>
          <span style={{ flex: 1 }}>{item}</span>
        </div>
      ))}
    </div>
  );
}

function ViewTag({ children, accent }) {
  return (
    <span style={{
      fontSize: 11, color: accent ? 'var(--brand)' : 'var(--text-muted)',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 4, padding: '1px 6px', marginLeft: 6, fontWeight: 500,
    }}>{children}</span>
  );
}
